import {
  CardActions,
  CardContent,
  CardHeader,
  Collapse,
  Divider,
  List,
  Stack,
  Typography,
} from '@mui/material';

import { use, useState } from 'react';

import type { Or2CardProps } from '@/components/Surface/Or2Card';
import { Or2Card } from '@/components/Surface/Or2Card';
import { graphql, type DocumentType } from '@/gql';
import { UserAvatar } from '@/modules/user/components/UserAvatar';
import { TimeAgo } from '@/components/date-time/TimeAgo';

import { Editor } from '@/modules/editor/Editor';

import { extractConnectionProps } from '@/utils/queries/queryExtracts';

import { CurrentUserContext } from '@/context/CurrentUserContext';

import { Or2ListItem } from '@/components/data-display/Or2ListItem';

import { getAuthorName } from '../noticeboardUtils';

import { PostPinButton } from './post/PostPinButton';
import { PostOptionsButton } from './post/PostOptionsButton';
import { PostReactionBar } from './post/PostReactionBar';
import { PostNotificationBar } from './post/PostNotificationBar';
import { PostReactionButton } from './post/PostReactionButton';
import { PostCommentButton } from './post/PostCommentButton';
import { PostComment } from './post/PostComment';
import { PostCommentEditor } from './post/PostCommentEditor';
import { PostOwnerBar } from './post/PostOwnerBar';
import { PostTagsBar } from './post/PostTagsBar';

const POST_POST_FRAGMENT = graphql(`
  fragment postPostFragment on Post {
    ...postFields
    ...postPinButtonPostFields
    ...postNotificationBarPostFragment
    ...postCommentsPostFields
    ...postCommentEditorPostFragment
    ...postOwnerBarPostFragment
    ...postTagsBarPostFragment
    ...postReactionBarPostFields
    _id
    htmlContent
    editorContent
    content
    commentsConnection(orderBy: [CREATED_DESC]) {
      edges {
        node {
          ...postCommentButtonCommentFields
          ...postCommentsCommentFields
        }
      }
    }
    reactionsConnection {
      edges {
        node {
          _id
        }
      }
    }
    hasPostLinkedObjectsConnection {
      edges {
        node {
          _id
        }
      }
    }
  }
`);

const POST_GROUP_FRAGMENT = graphql(`
  fragment postGroupFields on Group {
    ...postOptionsButtonGroupFragment
    ...postTagsBarGroupFragment
    ...postCommentsGroupFields
    ...postPinButtonGroupFields
    _id
    userCanEditAllPosts
  }
`);

interface Props extends Or2CardProps {
  post: DocumentType<typeof POST_POST_FRAGMENT>;
  group: DocumentType<typeof POST_GROUP_FRAGMENT>;
  showOwner?: boolean;
  getSubgroupColor: (subgroupId: string) => string;
  serviceMachineName?: string | null;
  location?: 'myHub' | 'adminHub' | null;
}

export const Post = ({
  post,
  group,
  showOwner,
  getSubgroupColor,
  serviceMachineName = null,
  location = null,
  ...other
}: Props) => {
  const [showComments, setShowComments] = useState(true);
  const { currentUserId } = use(CurrentUserContext);

  const {
    author,
    htmlContent,
    editorContent,
    content,
    created,
    title,
    commentsConnection,
    reactionsConnection,
    hasPostLinkedObjectsConnection,
  } = post ?? {};
  const { userCanEditAllPosts } = group ?? {};

  const authorName = getAuthorName(post);

  const canEditPost = author?._id === currentUserId || userCanEditAllPosts;

  const comments = extractConnectionProps(commentsConnection);
  const reactions = extractConnectionProps(reactionsConnection);
  const tags = extractConnectionProps(hasPostLinkedObjectsConnection);

  return (
    <Or2Card data-test="noticeboard-post" {...other}>
      {showOwner && <PostOwnerBar post={post} />}
      {tags.length ? <PostTagsBar post={post} group={group} /> : null}
      <CardHeader
        avatar={<UserAvatar size={60} user={author} />}
        title={authorName}
        slotProps={{ title: { variant: 'h6', sx: { fontSize: 16 } } }}
        subheader={<TimeAgo date={created} />}
        action={
          <Stack direction="row">
            {canEditPost ? (
              <PostPinButton
                post={post}
                group={group}
                serviceMachineName={serviceMachineName}
                location={location}
              />
            ) : null}
            <PostOptionsButton
              post={post}
              group={group}
              serviceMachineName={serviceMachineName}
              location={location}
            />
          </Stack>
        }
      />
      <CardContent>
        {title ? (
          <Typography variant="h6" sx={{ paddingLeft: 1.5 }}>
            {title}
          </Typography>
        ) : null}
        <Editor
          htmlContent={htmlContent}
          draftJsContent={editorContent}
          stringContent={content}
        />
      </CardContent>
      <Divider />
      <CardActions disableSpacing>
        <Stack spacing={1} sx={{ width: '100%' }}>
          {reactions.length > 0 ? <PostReactionBar post={post} /> : null}
          <Stack direction="row" justifyContent="space-between">
            <PostNotificationBar
              post={post}
              getSubgroupColor={getSubgroupColor}
            />
            <Stack direction="row" spacing={1}>
              <PostReactionButton post={post} />
              <PostCommentButton
                comments={comments}
                showComments={showComments}
                setShowComments={setShowComments}
              />
            </Stack>
          </Stack>
        </Stack>
      </CardActions>
      <Collapse in={showComments}>
        {comments.length ? (
          <>
            <Divider />
            <List>
              {comments.map((comment, index) => (
                <Or2ListItem
                  key={comment._id}
                  divider={index < comments.length - 1}
                >
                  <PostComment comment={comment} post={post} group={group} />
                </Or2ListItem>
              ))}
            </List>
          </>
        ) : null}
        <Divider />
        <PostCommentEditor post={post} />
      </Collapse>
    </Or2Card>
  );
};
